import { applyStoryEditChangesTolerantly, type StoryEditDocument } from './apply-edit-operations';
import type { StoryEditAction } from './types';

/** A story as the Story API returns it; edits apply to its latest version. */
export type StoryWithLatest = { storyVersions: { latest?: { data: object; settings: object } } };

/** The parts of a story that edit operations address; `story` is its root fields without `storyVersions`. */
export function storyEditDocumentOf(story: StoryWithLatest): StoryEditDocument {
	const { storyVersions, ...root } = story;
	const { latest } = storyVersions;
	if (!latest) throw new Error('Story has no latest version');
	return { story: root, settings: { ...latest.settings }, data: { ...latest.data } };
}

/** The story rebuilt from the document's root fields and parts; root fields the document dropped stay dropped. */
export function withStoryEditDocument<T extends StoryWithLatest>(story: T, document: StoryEditDocument): T {
	const { latest } = story.storyVersions;
	// The document's root fields came from a T, so the rebuilt object is one.
	return {
		...document.story,
		storyVersions: {
			...story.storyVersions,
			latest: { ...latest, data: document.data, settings: document.settings },
		},
	} as T;
}

/** Re-applies actions, in order, onto a newer story; skipped changes are counted, never fatal. */
export function rebaseStoryEdits<T extends StoryWithLatest>(
	story: T,
	actions: readonly StoryEditAction[]
): { story: T; skippedCount: number } {
	const changes = actions.flatMap(action => action.changes);
	const { document, skipped } = applyStoryEditChangesTolerantly(storyEditDocumentOf(story), changes);
	return { story: withStoryEditDocument(story, document), skippedCount: skipped.length };
}
